import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Progress,
  SimpleGrid,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useDropdownOptions } from "../../hooks/useDropdownOptions";
import { Icons } from "../../WinterCountIconFIle";
import CustomModal from "../Common/CustomModal/CustomModal";
import MappingModal from "./MappingModal";
import RosterTypeSelector from "./RosterTypeSelector";
import SearchCaseForRoster from "./SearchCaseForRoster";

const formatKey = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

export default function StepMappingReview({
  file,
  mappingResult: initialMappingResult,
  onConfirm,
  onBack,
  importId,
  setStep,
  deleteImport,
  startImport,
}) {
  const [selectedCase, setSelectedCase] = useState(null);
  const [selectedLinkType, setSelectedLinkType] = useState(null);

  const subBg = useColorModeValue("gray.50", "gray.700");
  const selectBg = useColorModeValue("#F7FAFC", "#2D3748");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isSearchCaseOpen,
    onOpen: onOpenSearchCase,
    onClose: onCloseSearchCase,
  } = useDisclosure();
  const [mapping, setMapping] = useState({});
  const [mappingResult, setMappingResult] = useState(initialMappingResult);
  const [modalKey, setModalKey] = useState(0);

  useEffect(() => {
    setMappingResult(initialMappingResult);
  }, [initialMappingResult]);

  // ── Link type options from codes hook ──
  const { options: linkTypeSelectOptions, isLoading } =
    useDropdownOptions("LINK_TYPES");

  const {
    title = "",
    description = "",
    importType = "",
    confidence = 0,
    coverage = 0,
    breakdown = [],
    importFields = [],
    extraCsvFields = [],
    unmappedRequiredFields = [],
  } = mappingResult || {};

  const requiredFields = useMemo(
    () => importFields.filter((f) => f.required).map((f) => f.name),
    [importFields],
  );

  useEffect(() => {
    if (mappingResult?.breakdown) {
      const initial = mappingResult.breakdown.reduce((acc, row) => {
        acc[row.csv_header] = row.matched_column || "";
        return acc;
      }, {});
      setMapping(initial);
    }
  }, [mappingResult]);

  const handleRosterSuccess = (data) => {
    setMappingResult(data);
    setModalKey((k) => k + 1);
  };

  if (!mappingResult) {
    return (
      <VStack py={10} spacing={3}>
        <Text color="gray.500">No mapping data available</Text>
      </VStack>
    );
  }

  const confidencePercent = Math.round(confidence * 100);
  const coveragePercent = Math.round(coverage * 100);
  const mappedValues = Object.values(mapping);

  const missingRequired = requiredFields.filter(
    (f) => !mappedValues.includes(f),
  );
  const mappedRequiredCount = requiredFields.filter((f) =>
    mappedValues.includes(f),
  ).length;
  const hasIssues = extraCsvFields.length > 0;

  const handleMappingChange = (csvHeader, newValue) => {
    setMapping((prev) => ({ ...prev, [csvHeader]: newValue }));
  };

  // react-select custom styles to match Chakra UI look & feel
  const reactSelectStyles = {
    container: (base) => ({
      ...base,
      minWidth: "200px",
      fontSize: "14px",
    }),
    control: (base, state) => ({
      ...base,
      backgroundColor: selectBg,
      borderColor: state.isFocused ? "#3182CE" : "#E2E8F0",
      borderRadius: "2px",
      boxShadow: state.isFocused ? "0 0 0 1px #3182CE" : "none",
      minHeight: "32px",
      height: "32px",
      cursor: "pointer",
      "&:hover": {
        borderColor: "#CBD5E0",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "inherit",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#A0AEC0",
      fontSize: "13px",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "4px",
      boxShadow:
        "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "13px",
      backgroundColor: state.isSelected
        ? "#3182CE"
        : state.isFocused
          ? "#EBF8FF"
          : "white",
      color: state.isSelected ? "white" : "inherit",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#BEE3F8",
      },
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "0 6px",
      color: "#718096",
    }),
  };

  return (
    <VStack spacing={5} align="stretch">
      {/* ── Header ── */}
      <Box>
        <Flex align="center" justify="space-between" gap={2} mb={1}>
          <Heading size="md" fontWeight="600">
            {title}
          </Heading>
          <Badge
            colorScheme="blue"
            borderRadius="full"
            px={3}
            py={0.5}
            fontSize="xs"
          >
            {importType}
          </Badge>
        </Flex>
        <Text fontSize="sm" color="gray.500">
          {description}
        </Text>
        {file && (
          <Badge
            mt={2}
            colorScheme="gray"
            borderRadius="full"
            px={3}
            fontSize="xs"
          >
            📄 {file.name}
          </Badge>
        )}

        {mappingResult?.outbreak && mappingResult?.outbreak.link && (
          <HStack mt={4} spacing={3} align="center">
            <Button
              leftIcon={<Icon as={Icons.SearchIcon} boxSize={4} />}
              mx={2}
              size="sm"
              bg="gray.200"
              border="none"
              color="black"
              borderRadius="sm"
              fontWeight="500"
              px={5}
              _hover={{ bg: "gray.400" }}
              _active={{ bg: "gray.600" }}
              onClick={onOpenSearchCase}
            >
              Search case
            </Button>

            {/* ── Link Type react-select dropdown ── */}
            <Box minW="200px">
              <Select
                options={linkTypeSelectOptions}
                value={selectedLinkType}
                onChange={setSelectedLinkType}
                placeholder="Select link type..."
                isClearable
                styles={reactSelectStyles}
                isLoading={isLoading}
              />
            </Box>
          </HStack>
        )}

        {/* ── Unmapped required fields warning ── */}
        {unmappedRequiredFields.length > 0 && (
          <Box
            mt={3}
            bg="red.50"
            border="1px solid"
            borderColor="red.200"
            borderRadius="md"
            px={4}
            py={3}
          >
            <Flex align="center" gap={2} mb={1}>
              <Text fontSize="xs" fontWeight="700" color="red.600">
                ⛔ {unmappedRequiredFields.length} required field
                {unmappedRequiredFields.length > 1 ? "s" : ""} not mapped
              </Text>
            </Flex>
            <Flex gap={2} flexWrap="wrap">
              {unmappedRequiredFields.map((f) => (
                <Badge
                  key={f}
                  colorScheme="red"
                  variant="subtle"
                  borderRadius="full"
                  fontSize="10px"
                  px={2}
                  py={0.5}
                >
                  {f}
                </Badge>
              ))}
            </Flex>
            <Text fontSize="xs" color="red.400" mt={2}>
              Open "Review &amp; Adjust Mapping" to assign these fields before
              confirming.
            </Text>
          </Box>
        )}
      </Box>

      {/* ── Metrics ── */}
      <SimpleGrid columns={4} spacing={2}>
        <Box bg={subBg} p={4} borderRadius="lg">
          <Text fontSize="xs" color="gray.500">
            Confidence
          </Text>
          <Text fontSize="2xl" fontWeight="600" color="blue.500">
            {confidencePercent}%
          </Text>
          <Progress
            value={confidencePercent}
            size="xs"
            colorScheme="blue"
            mt={2}
          />
        </Box>

        <Box bg={subBg} p={4} borderRadius="lg">
          <Text fontSize="xs" color="gray.500">
            Coverage
          </Text>
          <Text fontSize="2xl" fontWeight="600">
            {coveragePercent}%
          </Text>
          <Progress
            value={coveragePercent}
            size="xs"
            colorScheme="teal"
            mt={2}
          />
        </Box>

        {hasIssues ? (
          <Box bg="orange.50" p={4} borderRadius="lg">
            <Text fontSize="xs" fontWeight="600" color="orange.600">
              ⚠ Extra fields
            </Text>
            <Text fontSize="xs" color="orange.500" mt={0.5}>
              {extraCsvFields.join(", ")}
            </Text>
          </Box>
        ) : (
          <Box bg={subBg} p={4} borderRadius="lg">
            <Text fontSize="xs" color="gray.500">
              Required mapped
            </Text>
            <Text
              fontSize="2xl"
              fontWeight="600"
              color={
                mappedRequiredCount < requiredFields.length
                  ? "red.500"
                  : "green.500"
              }
            >
              {mappedRequiredCount} / {requiredFields.length}
            </Text>
            <Progress
              value={
                requiredFields.length
                  ? Math.round(
                      (mappedRequiredCount / requiredFields.length) * 100,
                    )
                  : 0
              }
              size="xs"
              colorScheme={
                mappedRequiredCount < requiredFields.length ? "red" : "green"
              }
              mt={2}
            />
          </Box>
        )}
        {selectedCase ? (
          <Box bg={subBg} p={4} borderRadius="lg">
            <Text fontSize="xs" color="gray.500">
              Selected case link type
            </Text>
            {Object.entries(selectedCase.data)
              .filter(([key]) => key !== "id" && key !== "PERSON_ID")
              .map(([key, value]) => {
                const date = moment(
                  value,
                  ["YYYY-MM-DD", "YYYYMMDD", moment.ISO_8601],
                  true,
                );
                const isDate = date.isValid();

                return (
                  <Flex key={key} fontSize="sm">
                    <Box w="40%">{formatKey(key)}</Box>
                    <Box mx={1}>:</Box>
                    <Box w="60%">
                      {isDate ? date.format("MMM D, YYYY") : (value ?? "N/A")}
                    </Box>
                  </Flex>
                );
              })}
          </Box>
        ) : null}
      </SimpleGrid>

      {/* ── Actions ── */}
      <Box textAlign="center">
        <Button
          size="sm"
          bg="teal.500"
          color="white"
          borderRadius="sm"
          border="none"
          fontWeight="500"
          px={5}
          _hover={{ bg: "teal.400" }}
          _active={{ bg: "teal.600" }}
          onClick={onOpen}
          mx={2}
        >
          Review &amp; Adjust Mapping ({breakdown.length} columns)
        </Button>
        <RosterTypeSelector
          importId={importId}
          onSuccess={handleRosterSuccess}
        />
        <Button
          leftIcon={<Icon as={Icons.CloseIcon} boxSize={4} />}
          mx={2}
          size="sm"
          bg="red.500"
          border="none"
          color="white"
          borderRadius="sm"
          fontWeight="500"
          px={5}
          _hover={{ bg: "red.400" }}
          _active={{ bg: "red.600" }}
          onClick={() => {
            deleteImport(importId);
            setStep("UPLOAD");
          }}
        >
          Abort Import
        </Button>
      </Box>

      <Divider />

      <MappingModal
        key={modalKey}
        isOpen={isOpen}
        onClose={onClose}
        breakdown={breakdown}
        mapping={mapping}
        importFields={importFields}
        onMappingChange={handleMappingChange}
        missingRequired={missingRequired}
        unmappedRequiredFields={unmappedRequiredFields}
        onBack={onBack}
        onConfirm={onConfirm}
        hasIssues={hasIssues}
        extraCsvFields={extraCsvFields}
        requiredFields={requiredFields}
        mappedRequiredCount={mappedRequiredCount}
        importId={importId}
        setStep={setStep}
        startImport={startImport}
        selectedCase={selectedCase}
        selectedLinkType={selectedLinkType}
        outbreak={mappingResult?.outbreak && mappingResult?.outbreak?.link}
      />

      <CustomModal
        size="6xl"
        isCentered
        title="Search Case and assign to Roster"
        isOpen={isSearchCaseOpen}
        onClose={onCloseSearchCase}
        bodyContent={
          <SearchCaseForRoster
            setSelectedCase={setSelectedCase}
            onClose={onCloseSearchCase}
          />
        }
      />
    </VStack>
  );
}
